import React, { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { CheckCircle2, Phone } from "lucide-react";
import { COMPANY } from "../mock";
import { Honeypot } from "./Honeypot";
import api from "../lib/api";

const EstimateCtx = createContext(null);
export const useEstimate = () => useContext(EstimateCtx);

export function EstimateProvider({ children }) {
  const [open, setOpen] = useState(false);
  const openEstimate = () => setOpen(true);
  return (
    <EstimateCtx.Provider value={{ openEstimate }}>
      {children}
      <EstimateModal open={open} setOpen={setOpen} />
    </EstimateCtx.Provider>
  );
}

function EstimateModal({ open, setOpen }) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [website, setWebsite] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, source: "estimate-form", website });
      setSubmitted(true);
      toast({ title: "Request received", description: "We'll reach out to you shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us.", variant: "destructive" });
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setWebsite("");
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden border-0">
        <div className="bg-[#0f2740] px-8 py-6">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-semibold tracking-tight">
              Get a Free Estimate
            </DialogTitle>
            <DialogDescription className="text-[#a9c6dd]">
              Tell us about your project and we'll get back to you within 24 hours.
            </DialogDescription>
          </DialogHeader>
        </div>

        {submitted ? (
          <div className="px-8 py-12 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#2f9fe6] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#0f2740] mb-2">Thank you!</h3>
            <p className="text-[#6b7683] mb-6">
              Your request has been received. Our team will contact you soon.
            </p>
            <button
              onClick={close}
              className="px-6 py-3 rounded-full bg-[#2f9fe6] text-white font-medium hover:bg-[#1f82c7] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
            <Honeypot value={website} onChange={setWebsite} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Doe" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(780) 000-0000" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Service</Label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger><SelectValue placeholder="Select a service" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kitchen">Kitchen Renovation</SelectItem>
                  <SelectItem value="bathroom">Bathroom Renovation</SelectItem>
                  <SelectItem value="basement">Basement Development</SelectItem>
                  <SelectItem value="custom">Custom Interiors</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="other">Other / Not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="message">Project details</Label>
              <Textarea id="message" rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us a bit about your space and goals..." />
            </div>
            <button type="submit" className="w-full py-3.5 rounded-full bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors">
              Send Request
            </button>
            <a href={COMPANY.phoneHref} className="flex items-center justify-center gap-2 text-sm text-[#6b7683] hover:text-[#2f9fe6] transition-colors">
              <Phone className="w-4 h-4" /> Or call us: {COMPANY.phone}
            </a>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
